import React, { useState, useEffect, useRef } from 'react';
import { Save, X, Plus, Users, BarChart3, Shield, Clock, Star, Edit2, Trash2, FileUp, Sparkles, CheckSquare, Square, Briefcase, GraduationCap, ChevronDown, ChevronUp, Globe, Award, BookOpen, Linkedin, Medal } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { DocumentAnalyzer, ExtractedPerson } from '../../lib/document-analysis';

// Work history entry structure
interface WorkHistoryEntry {
  company: string;
  title: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  description: string;
  achievements: string[];
  location?: string;
  employmentType?: string; // Full-time, Part-time, Contract, Volunteer, Internship
}

// Education history entry structure
interface EducationEntry {
  institution: string;
  degree: string;
  field: string;
  graduationYear: number | null;
  honors?: string;
  gpa?: string;
}

// Military service entry structure
interface MilitaryServiceEntry {
  branch: string;
  rank: string;
  startDate: string;
  endDate: string;
  yearsOfService: number;
  specialty: string;
  honors: string[];
  dischargeType: string;
  isVeteran: boolean;
  serviceConnectedDisability: boolean;
  deployments: string[];
}

interface Personnel {
  id?: string;
  // Original fields (preserve existing structure)
  name: string;
  title: string;
  email: string;
  phone: string;
  linkedinUrl: string;
  resumeUrl: string;
  // Enhanced fields (NEW)
  yearsExperience: number;
  securityClearance: string;
  skillsExpertise: string[];
  certifications: string[];
  availability: number;
  billableRate: number;
  educationLevel: string;
  keyAchievements: string[];
  // Resume-like fields
  professionalSummary: string;
  workHistory: WorkHistoryEntry[];
  educationHistory: EducationEntry[];
  publications: string[];
  languages: string[];
  awards: string[];
  // Military service
  militaryService: MilitaryServiceEntry | null;
}

export function KeyPersonnel() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Resume import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [extractedPersonnel, setExtractedPersonnel] = useState<ExtractedPerson[]>([]);
  const [selectedImports, setSelectedImports] = useState<Set<number>>(new Set());

  const [newPerson, setNewPerson] = useState<Personnel>({
    // Original fields
    name: '',
    title: '',
    email: '',
    phone: '',
    linkedinUrl: '',
    resumeUrl: '',
    // Enhanced fields with defaults
    yearsExperience: 0,
    securityClearance: 'none',
    skillsExpertise: [],
    certifications: [],
    availability: 100,
    billableRate: 0,
    educationLevel: '',
    keyAchievements: [],
    // Resume-like fields
    professionalSummary: '',
    workHistory: [],
    educationHistory: [],
    publications: [],
    languages: [],
    awards: [],
    // Military service
    militaryService: null
  });

  // State for expanded sections in the form
  const [expandedSections, setExpandedSections] = useState({
    workHistory: true,
    educationHistory: true,
    militaryService: false,
    additionalInfo: false
  });

  // State for adding new work history entry
  const [newWorkEntry, setNewWorkEntry] = useState<WorkHistoryEntry>({
    company: '',
    title: '',
    startDate: '',
    endDate: '',
    isCurrent: false,
    description: '',
    achievements: [],
    location: '',
    employmentType: 'Full-time'
  });

  // State for adding new education entry
  const [newEducationEntry, setNewEducationEntry] = useState<EducationEntry>({
    institution: '',
    degree: '',
    field: '',
    graduationYear: null,
    honors: '',
    gpa: ''
  });

  useEffect(() => {
    if (profile?.id) {
      fetchPersonnel();
    }
  }, [profile?.id]);

  const fetchPersonnel = async () => {
    if (!profile?.id) return;

    try {
      const { data, error } = await supabase
        .from('key_personnel')
        .select('*')
        .eq('profile_id', profile.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // SAFE: Map database fields to component structure
      const mappedPersonnel = (data || []).map(person => ({
        id: person.id,
        // Original fields (use existing database field names)
        name: person.name || '',
        title: person.title || '',
        email: person.email || '',
        phone: person.phone || '',
        linkedinUrl: person.linkedin_url || '',
        resumeUrl: person.resume_url || '',
        // Enhanced fields (new database fields)
        yearsExperience: person.years_experience || 0,
        securityClearance: person.security_clearance || 'none',
        skillsExpertise: person.skills_expertise || [],
        certifications: person.certifications || [],
        availability: person.availability || 100,
        billableRate: person.billable_rate || 0,
        educationLevel: person.education_level || '',
        keyAchievements: person.key_achievements || [],
        // Resume-like fields (JSONB and arrays)
        professionalSummary: person.professional_summary || '',
        workHistory: person.work_history || [],
        educationHistory: person.education_history || [],
        publications: person.publications || [],
        languages: person.languages || [],
        awards: person.awards || [],
        // Military service
        militaryService: person.military_service || null
      }));
      
      setPersonnel(mappedPersonnel);
    } catch (error) {
      console.error('Error fetching personnel:', error);
      toast.error('Failed to load personnel data');
    }
  };

  // Enhanced: Calculate team intelligence score
  const calculateTeamScore = () => {
    if (personnel.length === 0) return 0;

    let score = 0;
    const maxScore = 100;

    // Team size (20 points)
    const teamSizeScore = Math.min(personnel.length / 5, 1) * 20;
    score += teamSizeScore;

    // Average experience (25 points)
    const avgExperience = personnel.reduce((sum, p) => sum + p.yearsExperience, 0) / personnel.length;
    const experienceScore = Math.min(avgExperience / 10, 1) * 25;
    score += experienceScore;

    // Security clearances (20 points)
    const clearanceCount = personnel.filter(p => p.securityClearance !== 'none').length;
    const clearanceScore = personnel.length > 0 ? (clearanceCount / personnel.length) * 20 : 0;
    score += clearanceScore;

    // Skills diversity (15 points)
    const allSkills = personnel.flatMap(p => p.skillsExpertise);
    const uniqueSkills = [...new Set(allSkills)];
    const skillsScore = Math.min(uniqueSkills.length / 10, 1) * 15;
    score += skillsScore;

    // Certifications (10 points)
    const allCerts = personnel.flatMap(p => p.certifications);
    const certsScore = Math.min(allCerts.length / 5, 1) * 10;
    score += certsScore;

    // Complete profiles (10 points)
    const completeProfiles = personnel.filter(p => 
      p.name && p.title && p.email && p.yearsExperience > 0
    ).length;
    const profileScore = personnel.length > 0 ? (completeProfiles / personnel.length) * 10 : 0;
    score += profileScore;

    return Math.round(Math.min(score, maxScore));
  };

  const teamScore = calculateTeamScore();

  const addToPersonnelArray = (field: 'skillsExpertise' | 'certifications' | 'keyAchievements' | 'publications' | 'languages' | 'awards', value: string) => {
    if (!value.trim()) return;

    if (editingIndex !== null) {
      const updatedPersonnel = [...personnel];
      updatedPersonnel[editingIndex] = {
        ...updatedPersonnel[editingIndex],
        [field]: [...(updatedPersonnel[editingIndex][field] || []), value.trim()]
      };
      setPersonnel(updatedPersonnel);
    } else {
      setNewPerson(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value.trim()]
      }));
    }
  };

  const removeFromPersonnelArray = (field: 'skillsExpertise' | 'certifications' | 'keyAchievements' | 'publications' | 'languages' | 'awards', itemIndex: number) => {
    if (editingIndex !== null) {
      const updatedPersonnel = [...personnel];
      updatedPersonnel[editingIndex] = {
        ...updatedPersonnel[editingIndex],
        [field]: updatedPersonnel[editingIndex][field].filter((_, i) => i !== itemIndex)
      };
      setPersonnel(updatedPersonnel);
    } else {
      setNewPerson(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== itemIndex)
      }));
    }
  };

  // Work history management functions
  const addWorkHistoryEntry = () => {
    if (!newWorkEntry.company || !newWorkEntry.title) {
      toast.error('Company and Title are required');
      return;
    }

    const entry = { ...newWorkEntry };
    if (editingIndex !== null) {
      const updated = [...personnel];
      updated[editingIndex] = {
        ...updated[editingIndex],
        workHistory: [...(updated[editingIndex].workHistory || []), entry]
      };
      setPersonnel(updated);
    } else {
      setNewPerson(prev => ({
        ...prev,
        workHistory: [...prev.workHistory, entry]
      }));
    }

    setNewWorkEntry({
      company: '', title: '', startDate: '', endDate: '', isCurrent: false,
      description: '', achievements: [], location: '', employmentType: 'Full-time'
    });
  };

  const removeWorkHistoryEntry = (index: number) => {
    if (editingIndex !== null) {
      const updated = [...personnel];
      updated[editingIndex] = {
        ...updated[editingIndex],
        workHistory: updated[editingIndex].workHistory.filter((_, i) => i !== index)
      };
      setPersonnel(updated);
    } else {
      setNewPerson(prev => ({
        ...prev,
        workHistory: prev.workHistory.filter((_, i) => i !== index)
      }));
    }
  };

  // Education history management functions
  const addEducationEntry = () => {
    if (!newEducationEntry.institution || !newEducationEntry.degree) {
      toast.error('Institution and Degree are required');
      return;
    }

    const entry = { ...newEducationEntry };
    if (editingIndex !== null) {
      const updated = [...personnel];
      updated[editingIndex] = {
        ...updated[editingIndex],
        educationHistory: [...(updated[editingIndex].educationHistory || []), entry]
      };
      setPersonnel(updated);
    } else {
      setNewPerson(prev => ({
        ...prev,
        educationHistory: [...prev.educationHistory, entry]
      }));
    }

    setNewEducationEntry({
      institution: '', degree: '', field: '', graduationYear: null, honors: '', gpa: ''
    });
  };

  const removeEducationEntry = (index: number) => {
    if (editingIndex !== null) {
      const updated = [...personnel];
      updated[editingIndex] = {
        ...updated[editingIndex],
        educationHistory: updated[editingIndex].educationHistory.filter((_, i) => i !== index)
      };
      setPersonnel(updated);
    } else {
      setNewPerson(prev => ({
        ...prev,
        educationHistory: prev.educationHistory.filter((_, i) => i !== index)
      }));
    }
  };

  // Toggle section expansion
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get current work history and education history for form
  const currentWorkHistory = editingIndex !== null
    ? personnel[editingIndex]?.workHistory || []
    : newPerson.workHistory;

  const currentEducationHistory = editingIndex !== null
    ? personnel[editingIndex]?.educationHistory || []
    : newPerson.educationHistory;

  const handleAddPerson = async () => {
    if (!profile?.id || !newPerson.name || !newPerson.title) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('key_personnel')
        .insert({
          profile_id: profile.id,
          // Original fields (use existing database field names)
          name: newPerson.name,
          title: newPerson.title,
          email: newPerson.email,
          phone: newPerson.phone,
          linkedin_url: newPerson.linkedinUrl,
          resume_url: newPerson.resumeUrl,
          // Enhanced fields (use new database field names)
          years_experience: newPerson.yearsExperience,
          security_clearance: newPerson.securityClearance,
          skills_expertise: newPerson.skillsExpertise,
          certifications: newPerson.certifications,
          availability: newPerson.availability,
          billable_rate: newPerson.billableRate,
          education_level: newPerson.educationLevel,
          key_achievements: newPerson.keyAchievements,
          // Resume-like fields (JSONB and arrays)
          professional_summary: newPerson.professionalSummary || null,
          work_history: newPerson.workHistory.length > 0 ? newPerson.workHistory : null,
          education_history: newPerson.educationHistory.length > 0 ? newPerson.educationHistory : null,
          publications: newPerson.publications.length > 0 ? newPerson.publications : null,
          languages: newPerson.languages.length > 0 ? newPerson.languages : null,
          awards: newPerson.awards.length > 0 ? newPerson.awards : null,
          // Military service
          military_service: newPerson.militaryService || null
        });

      if (error) throw error;

      toast.success('Personnel added successfully');
      setNewPerson({
        name: '', title: '', email: '', phone: '', linkedinUrl: '', resumeUrl: '',
        yearsExperience: 0, securityClearance: 'none', skillsExpertise: [],
        certifications: [], availability: 100, billableRate: 0, educationLevel: '',
        keyAchievements: [], professionalSummary: '', workHistory: [], educationHistory: [],
        publications: [], languages: [], awards: [], militaryService: null
      });
      setShowAddForm(false);
      fetchPersonnel();
    } catch (error) {
      console.error('Error adding personnel:', error);
      toast.error('Failed to add personnel');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePerson = async (index: number) => {
    if (!profile?.id) return;

    const person = personnel[index];
    if (!person.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('key_personnel')
        .update({
          // Original fields (use existing database field names)
          name: person.name,
          title: person.title,
          email: person.email,
          phone: person.phone,
          linkedin_url: person.linkedinUrl,
          resume_url: person.resumeUrl,
          // Enhanced fields (use new database field names)
          years_experience: person.yearsExperience,
          security_clearance: person.securityClearance,
          skills_expertise: person.skillsExpertise,
          certifications: person.certifications,
          availability: person.availability,
          billable_rate: person.billableRate,
          education_level: person.educationLevel,
          key_achievements: person.keyAchievements,
          // Resume-like fields (JSONB and arrays)
          professional_summary: person.professionalSummary || null,
          work_history: person.workHistory?.length > 0 ? person.workHistory : null,
          education_history: person.educationHistory?.length > 0 ? person.educationHistory : null,
          publications: person.publications?.length > 0 ? person.publications : null,
          languages: person.languages?.length > 0 ? person.languages : null,
          awards: person.awards?.length > 0 ? person.awards : null,
          // Military service
          military_service: person.militaryService || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', person.id);

      if (error) throw error;

      toast.success('Personnel updated successfully');
      setEditingIndex(null);
    } catch (error) {
      console.error('Error updating personnel:', error);
      toast.error('Failed to update personnel');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePerson = async (index: number) => {
    const person = personnel[index];
    if (!person.id) return;

    if (!confirm('Are you sure you want to delete this person?')) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('key_personnel')
        .delete()
        .eq('id', person.id);

      if (error) throw error;

      toast.success('Personnel deleted successfully');
      fetchPersonnel();
    } catch (error) {
      console.error('Error deleting personnel:', error);
      toast.error('Failed to delete personnel');
    } finally {
      setLoading(false);
    }
  };

  const SecurityClearanceOptions = ['none', 'Public Trust', 'Secret', 'Top Secret', 'TS/SCI'];

  // Resume import handlers
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const result = await DocumentAnalyzer.processAndExtract(file, {
        documentType: 'Resume',
        extractionMode: 'personnel'
      });

      if (result.success && result.personnel.length > 0) {
        setExtractedPersonnel(result.personnel);
        setSelectedImports(new Set(result.personnel.map((_, i) => i)));
        setShowImportModal(true);
      } else {
        toast.error('No personnel data found in document');
      }
    } catch (error) {
      console.error('Error extracting resume data:', error);
      toast.error('Failed to extract data from document');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const toggleImportSelection = (index: number) => {
    setSelectedImports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const findExistingPerson = (extracted: ExtractedPerson): Personnel | undefined => {
    // Match by email first (most reliable)
    if (extracted.email) {
      const byEmail = personnel.find(p =>
        p.email?.toLowerCase() === extracted.email?.toLowerCase()
      );
      if (byEmail) return byEmail;
    }

    // Match by name (fuzzy match - case insensitive)
    const normalizedName = extracted.name.toLowerCase().trim();
    return personnel.find(p =>
      p.name.toLowerCase().trim() === normalizedName
    );
  };

  const handleImportSelected = async () => {
    if (!profile?.id || selectedImports.size === 0) return;

    setImportLoading(true);
    let imported = 0;
    let updated = 0;
    let errors = 0;

    for (const index of selectedImports) {
      const extracted = extractedPersonnel[index];
      const existing = findExistingPerson(extracted);

      try {
        if (existing?.id) {
          // Update existing person - merge data
          const updateData: Record<string, any> = {
            updated_at: new Date().toISOString()
          };

          // Only update fields that have new values
          if (extracted.title && extracted.title !== existing.title) {
            updateData.title = extracted.title;
          }
          if (extracted.yearsExperience && extracted.yearsExperience > (existing.yearsExperience || 0)) {
            updateData.years_experience = extracted.yearsExperience;
          }
          if (extracted.securityClearance && extracted.securityClearance !== 'none') {
            updateData.security_clearance = extracted.securityClearance;
          }
          if (extracted.educationLevel) {
            updateData.education_level = extracted.educationLevel;
          }
          if (extracted.phone && !existing.phone) {
            updateData.phone = extracted.phone;
          }
          if (extracted.email && !existing.email) {
            updateData.email = extracted.email;
          }

          // Merge arrays
          if (extracted.certifications?.length > 0) {
            const existingCerts = existing.certifications || [];
            updateData.certifications = [...new Set([...existingCerts, ...extracted.certifications])];
          }
          if (extracted.skillsExpertise?.length > 0) {
            const existingSkills = existing.skillsExpertise || [];
            updateData.skills_expertise = [...new Set([...existingSkills, ...extracted.skillsExpertise])];
          }
          if (extracted.keyAchievements?.length > 0) {
            const existingAchievements = existing.keyAchievements || [];
            updateData.key_achievements = [...new Set([...existingAchievements, ...extracted.keyAchievements])];
          }

          // Rich resume fields - update if extracted has them
          if (extracted.professionalSummary) {
            updateData.professional_summary = extracted.professionalSummary;
          }
          if (extracted.workHistory?.length > 0) {
            // Merge work history entries - avoid duplicates by company+title
            const existingHistory = existing.workHistory || [];
            const newEntries = extracted.workHistory.filter((newJob: any) =>
              !existingHistory.some((existingJob: any) =>
                existingJob.company === newJob.company && existingJob.title === newJob.title
              )
            );
            updateData.work_history = [...existingHistory, ...newEntries];
          }
          if (extracted.educationHistory?.length > 0) {
            // Merge education history - avoid duplicates by institution+degree
            const existingEducation = existing.educationHistory || [];
            const newEntries = extracted.educationHistory.filter((newEdu: any) =>
              !existingEducation.some((existingEdu: any) =>
                existingEdu.institution === newEdu.institution && existingEdu.degree === newEdu.degree
              )
            );
            updateData.education_history = [...existingEducation, ...newEntries];
          }
          if (extracted.publications?.length > 0) {
            const existingPubs = existing.publications || [];
            updateData.publications = [...new Set([...existingPubs, ...extracted.publications])];
          }
          if (extracted.languages?.length > 0) {
            const existingLangs = existing.languages || [];
            updateData.languages = [...new Set([...existingLangs, ...extracted.languages])];
          }
          if (extracted.awards?.length > 0) {
            const existingAwards = existing.awards || [];
            updateData.awards = [...new Set([...existingAwards, ...extracted.awards])];
          }

          if (Object.keys(updateData).length > 1) { // More than just updated_at
            const { error } = await supabase
              .from('key_personnel')
              .update(updateData)
              .eq('id', existing.id);

            if (error) throw error;
            updated++;
          }
        } else {
          // Insert new person with all extracted fields
          const { error } = await supabase
            .from('key_personnel')
            .insert({
              profile_id: profile.id,
              name: extracted.name,
              title: extracted.title,
              email: extracted.email || null,
              phone: extracted.phone || null,
              years_experience: extracted.yearsExperience || null,
              security_clearance: extracted.securityClearance || null,
              education_level: extracted.educationLevel || null,
              certifications: extracted.certifications?.length > 0 ? extracted.certifications : null,
              skills_expertise: extracted.skillsExpertise?.length > 0 ? extracted.skillsExpertise : null,
              key_achievements: extracted.keyAchievements?.length > 0 ? extracted.keyAchievements : null,
              // Rich resume fields
              professional_summary: extracted.professionalSummary || null,
              work_history: extracted.workHistory?.length > 0 ? extracted.workHistory : null,
              education_history: extracted.educationHistory?.length > 0 ? extracted.educationHistory : null,
              publications: extracted.publications?.length > 0 ? extracted.publications : null,
              languages: extracted.languages?.length > 0 ? extracted.languages : null,
              awards: extracted.awards?.length > 0 ? extracted.awards : null
            });

          if (error) throw error;
          imported++;
        }
      } catch (error) {
        console.error('Error importing person:', extracted.name, error);
        errors++;
      }
    }

    if (imported > 0 || updated > 0) {
      const message = [];
      if (imported > 0) message.push(`${imported} added`);
      if (updated > 0) message.push(`${updated} updated`);
      toast.success(`Personnel ${message.join(', ')}`);
      fetchPersonnel();
    }
    if (errors > 0) {
      toast.error(`Failed to import ${errors} personnel`);
    }

    setShowImportModal(false);
    setExtractedPersonnel([]);
    setSelectedImports(new Set());
    setImportLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Enhanced: Team Intelligence Dashboard */}
      <div className="bg-bw-surface-subtle rounded-lg p-6 border border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-bw-ink flex items-center">
            <Users className="h-5 w-5 mr-2 text-bw-primary" />
            Team Intelligence
          </h3>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            teamScore >= 80 ? 'text-success bg-success-bg' :
            teamScore >= 60 ? 'text-fit-moderate bg-fit-moderate-bg' : 'text-urgent bg-urgent-bg'
          }`}>
            {teamScore}% Complete
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Team Score</span>
              <span className="text-lg font-bold text-bw-ink">{teamScore}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  teamScore >= 80 ? 'bg-success-bg0' :
                  teamScore >= 60 ? 'bg-fit-moderate-bg0' : 'bg-urgent-bg0'
                }`}
                style={{ width: `${teamScore}%` }}
              />
            </div>
          </div>

          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Team Size</span>
              <span className="text-lg font-bold text-bw-primary">{personnel.length}</span>
            </div>
            <div className="text-xs text-bw-muted mt-1">Recommended: 5+</div>
          </div>

          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Avg Experience</span>
              <span className="text-lg font-bold text-bw-primary">
                {personnel.length > 0 ? Math.round(personnel.reduce((sum, p) => sum + p.yearsExperience, 0) / personnel.length) : 0}yr
              </span>
            </div>
            <div className="text-xs text-bw-muted mt-1">Target: 10+ years</div>
          </div>

          <div className="bg-bw-surface rounded-lg p-4 border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-bw-muted">Clearances</span>
              <span className="text-lg font-bold text-bw-primary">
                {personnel.filter(p => p.securityClearance !== 'none').length}
              </span>
            </div>
            <div className="text-xs text-bw-muted mt-1">Security cleared</div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-bw-surface rounded-lg border">
          <div className="flex items-center text-sm text-bw-muted">
            <BarChart3 className="h-4 w-4 mr-2" />
            <span>Next Steps: </span>
            {teamScore < 60 && (
              <span className="text-urgent ml-1">Add more team members and improve skill coverage</span>
            )}
            {teamScore >= 60 && teamScore < 80 && (
              <span className="text-fit-moderate ml-1">Enhance security clearances and certifications</span>
            )}
            {teamScore >= 80 && (
              <span className="text-success ml-1">Excellent team profile! Consider specialized expertise areas</span>
            )}
          </div>
        </div>
      </div>

      {/* Add Person Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-bw-ink">Key Personnel</h2>
        <div className="flex space-x-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importLoading}
            className="flex items-center px-4 py-2 border border-blue-600 text-bw-primary rounded-md hover:bg-bw-primary-tint disabled:opacity-50"
          >
            {importLoading ? (
              <>
                <span className="animate-spin mr-2">&#9696;</span>
                Extracting...
              </>
            ) : (
              <>
                <FileUp className="h-4 w-4 mr-2" />
                Import from Resume
              </>
            )}
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Person
          </button>
        </div>
      </div>

      {/* Resume Import Modal */}
      {showImportModal && extractedPersonnel.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bw-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b bg-bw-surface-subtle">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Sparkles className="h-5 w-5 text-bw-primary mr-2" />
                  <h3 className="text-lg font-semibold text-bw-ink">
                    Extracted Personnel ({extractedPersonnel.length})
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setExtractedPersonnel([]);
                  }}
                  className="text-bw-muted hover:text-bw-muted"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-bw-muted mt-1">
                Select personnel to import. Matching names/emails will update existing records.
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {extractedPersonnel.map((person, index) => {
                const existingMatch = findExistingPerson(person);
                return (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      selectedImports.has(index) ? 'border-blue-500 bg-bw-primary-tint' : 'border-bw-border'
                    }`}
                  >
                    <div className="flex items-start">
                      <button
                        onClick={() => toggleImportSelection(index)}
                        className="mr-3 mt-1"
                      >
                        {selectedImports.has(index) ? (
                          <CheckSquare className="h-5 w-5 text-bw-primary" />
                        ) : (
                          <Square className="h-5 w-5 text-bw-muted" />
                        )}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold text-bw-ink">{person.name}</span>
                            {existingMatch && (
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-fit-moderate-bg text-fit-moderate">
                                Will update existing
                              </span>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            person.confidence >= 0.8 ? 'bg-success-bg text-success' :
                            person.confidence >= 0.6 ? 'bg-fit-moderate-bg text-fit-moderate' :
                            'bg-urgent-bg text-urgent'
                          }`}>
                            {Math.round(person.confidence * 100)}% confidence
                          </span>
                        </div>
                        <p className="text-sm text-bw-muted mt-1">{person.title}</p>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-bw-muted">
                          {person.email && <span>Email: {person.email}</span>}
                          {person.yearsExperience && <span>{person.yearsExperience} years exp</span>}
                          {person.securityClearance && (
                            <span className="text-bw-primary">{person.securityClearance}</span>
                          )}
                          {person.educationLevel && <span>{person.educationLevel}</span>}
                        </div>
                        {person.skillsExpertise?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {person.skillsExpertise.slice(0, 5).map((skill, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-gray-200 rounded">
                                {skill}
                              </span>
                            ))}
                            {person.skillsExpertise.length > 5 && (
                              <span className="text-xs text-bw-muted">
                                +{person.skillsExpertise.length - 5} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-6 py-4 border-t bg-bw-surface-subtle flex justify-between items-center">
              <span className="text-sm text-bw-muted">
                {selectedImports.size} of {extractedPersonnel.length} selected
              </span>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setExtractedPersonnel([]);
                  }}
                  className="px-4 py-2 border border-bw-border rounded-md text-bw-ink hover:bg-bw-surface-subtle"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportSelected}
                  disabled={importLoading || selectedImports.size === 0}
                  className="px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {importLoading ? (
                    <>
                      <span className="animate-spin mr-2">&#9696;</span>
                      Importing...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Import Selected
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Person Form */}
      {(showAddForm || editingIndex !== null) && (
        <div className="bg-bw-surface rounded-lg border p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              {editingIndex !== null ? 'Edit Person' : 'Add New Person'}
            </h3>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingIndex(null);
              }}
              className="text-bw-muted hover:text-bw-muted"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Name *
              </label>
              <input
                type="text"
                value={editingIndex !== null ? personnel[editingIndex]?.name || '' : newPerson.name}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], name: e.target.value };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, name: e.target.value }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Title *
              </label>
              <input
                type="text"
                value={editingIndex !== null ? personnel[editingIndex]?.title || '' : newPerson.title}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], title: e.target.value };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, title: e.target.value }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Email
              </label>
              <input
                type="email"
                value={editingIndex !== null ? personnel[editingIndex]?.email || '' : newPerson.email}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], email: e.target.value };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, email: e.target.value }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={editingIndex !== null ? personnel[editingIndex]?.phone || '' : newPerson.phone}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], phone: e.target.value };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, phone: e.target.value }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                placeholder="(555) 123-4567"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-bw-ink mb-1">
                <div className="flex items-center">
                  <Linkedin className="h-4 w-4 mr-2 text-[#0077B5]" />
                  LinkedIn Profile
                </div>
              </label>
              <input
                type="url"
                value={editingIndex !== null ? personnel[editingIndex]?.linkedinUrl || '' : newPerson.linkedinUrl}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], linkedinUrl: e.target.value };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, linkedinUrl: e.target.value }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
                placeholder="https://linkedin.com/in/..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Years Experience
              </label>
              <input
                type="number"
                min="0"
                value={editingIndex !== null ? personnel[editingIndex]?.yearsExperience || 0 : newPerson.yearsExperience}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], yearsExperience: parseInt(e.target.value) || 0 };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, yearsExperience: parseInt(e.target.value) || 0 }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Security Clearance
              </label>
              <select
                value={editingIndex !== null ? personnel[editingIndex]?.securityClearance || 'none' : newPerson.securityClearance}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], securityClearance: e.target.value };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, securityClearance: e.target.value }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
              >
                {SecurityClearanceOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Education Level
              </label>
              <select
                value={editingIndex !== null ? personnel[editingIndex]?.educationLevel || '' : newPerson.educationLevel}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], educationLevel: e.target.value };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, educationLevel: e.target.value }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
              >
                <option value="">Select education level...</option>
                <option value="High School">High School</option>
                <option value="Associate's">Associate's Degree</option>
                <option value="Bachelor's">Bachelor's Degree</option>
                <option value="Master's">Master's Degree</option>
                <option value="PhD">PhD/Doctorate</option>
                <option value="Professional">Professional Degree (MD, JD, etc.)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-bw-ink mb-1">
                Availability (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={editingIndex !== null ? personnel[editingIndex]?.availability || 100 : newPerson.availability}
                onChange={(e) => {
                  if (editingIndex !== null) {
                    const updated = [...personnel];
                    updated[editingIndex] = { ...updated[editingIndex], availability: parseInt(e.target.value) || 100 };
                    setPersonnel(updated);
                  } else {
                    setNewPerson(prev => ({ ...prev, availability: parseInt(e.target.value) || 100 }));
                  }
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
              />
            </div>
          </div>

          {/* Skills Expertise */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-bw-ink mb-2">
              Skills & Expertise
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(editingIndex !== null ? personnel[editingIndex]?.skillsExpertise || [] : newPerson.skillsExpertise).map((skill, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-bw-primary-tint text-bw-primary">
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeFromPersonnelArray('skillsExpertise', index)}
                    className="ml-2 text-bw-primary hover:text-bw-primary"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add skill..."
                className="flex-1 rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToPersonnelArray('skillsExpertise', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                  addToPersonnelArray('skillsExpertise', input.value);
                  input.value = '';
                }}
                className="px-3 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 text-sm"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Certifications */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-bw-ink mb-2">
              Certifications
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(editingIndex !== null ? personnel[editingIndex]?.certifications || [] : newPerson.certifications).map((cert, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-bw-primary-tint text-bw-primary">
                  {cert}
                  <button
                    type="button"
                    onClick={() => removeFromPersonnelArray('certifications', index)}
                    className="ml-2 text-bw-primary hover:text-bw-primary"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add certification (e.g., PMP, CISSP, AWS)..."
                className="flex-1 rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToPersonnelArray('certifications', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                  addToPersonnelArray('certifications', input.value);
                  input.value = '';
                }}
                className="px-3 py-2 bg-bw-primary text-white rounded-md hover:bg-purple-700 text-sm"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Key Achievements */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-bw-ink mb-2">
              Key Achievements
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(editingIndex !== null ? personnel[editingIndex]?.keyAchievements || [] : newPerson.keyAchievements).map((achievement, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-success-bg text-success max-w-full">
                  <span className="truncate">{achievement}</span>
                  <button
                    type="button"
                    onClick={() => removeFromPersonnelArray('keyAchievements', index)}
                    className="ml-2 text-success hover:text-success flex-shrink-0"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add achievement..."
                className="flex-1 rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToPersonnelArray('keyAchievements', e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                  addToPersonnelArray('keyAchievements', input.value);
                  input.value = '';
                }}
                className="px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Professional Summary */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-bw-ink mb-2">
              Professional Summary
            </label>
            <textarea
              value={editingIndex !== null ? personnel[editingIndex]?.professionalSummary || '' : newPerson.professionalSummary}
              onChange={(e) => {
                if (editingIndex !== null) {
                  const updated = [...personnel];
                  updated[editingIndex] = { ...updated[editingIndex], professionalSummary: e.target.value };
                  setPersonnel(updated);
                } else {
                  setNewPerson(prev => ({ ...prev, professionalSummary: e.target.value }));
                }
              }}
              rows={4}
              className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
              placeholder="A brief professional bio summarizing qualifications, experience, and expertise areas..."
            />
          </div>

          {/* Work History Section */}
          <div className="mt-6 border-t pt-6">
            <button
              type="button"
              onClick={() => toggleSection('workHistory')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <Briefcase className="h-5 w-5 text-bw-primary mr-2" />
                <span className="text-lg font-semibold text-bw-ink">Work History</span>
                <span className="ml-2 text-sm text-bw-muted">({currentWorkHistory.length} entries)</span>
              </div>
              {expandedSections.workHistory ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.workHistory && (
              <div className="mt-4 space-y-4">
                {/* Existing work history entries */}
                {currentWorkHistory.map((entry, index) => (
                  <div key={index} className="bg-bw-surface-subtle rounded-lg p-4 border relative">
                    <button
                      type="button"
                      onClick={() => removeWorkHistoryEntry(index)}
                      className="absolute top-2 right-2 text-bw-muted hover:text-urgent"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="pr-8">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-bw-ink">{entry.title}</h4>
                        <span className="text-xs text-bw-muted">{entry.employmentType}</span>
                      </div>
                      <p className="text-sm text-bw-primary">{entry.company}</p>
                      <p className="text-xs text-bw-muted mt-1">
                        {entry.startDate} - {entry.isCurrent ? 'Present' : entry.endDate}
                        {entry.location && ` | ${entry.location}`}
                      </p>
                      {entry.description && (
                        <p className="text-sm text-bw-muted mt-2">{entry.description}</p>
                      )}
                      {entry.achievements && entry.achievements.length > 0 && (
                        <ul className="mt-2 text-sm text-bw-muted list-disc list-inside">
                          {entry.achievements.map((ach, i) => (
                            <li key={i}>{ach}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add new work history entry form */}
                <div className="bg-bw-primary-tint rounded-lg p-4 border border-blue-200">
                  <h4 className="font-medium text-bw-ink mb-3 flex items-center">
                    <Plus className="h-4 w-4 mr-2 text-bw-primary" />
                    Add Work Experience
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Company/Organization *</label>
                      <input
                        type="text"
                        value={newWorkEntry.company}
                        onChange={(e) => setNewWorkEntry(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Job Title *</label>
                      <input
                        type="text"
                        value={newWorkEntry.title}
                        onChange={(e) => setNewWorkEntry(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                        placeholder="Your title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Employment Type</label>
                      <select
                        value={newWorkEntry.employmentType}
                        onChange={(e) => setNewWorkEntry(prev => ({ ...prev, employmentType: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                      >
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Contract">Contract</option>
                        <option value="Consultant">Consultant</option>
                        <option value="Volunteer">Volunteer</option>
                        <option value="Internship">Internship</option>
                        <option value="Military">Military Service</option>
                        <option value="Non-profit">Non-profit</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Location</label>
                      <input
                        type="text"
                        value={newWorkEntry.location || ''}
                        onChange={(e) => setNewWorkEntry(prev => ({ ...prev, location: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                        placeholder="City, State"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Start Date</label>
                      <input
                        type="text"
                        value={newWorkEntry.startDate}
                        onChange={(e) => setNewWorkEntry(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                        placeholder="MM/YYYY or Year"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">End Date</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newWorkEntry.endDate}
                          onChange={(e) => setNewWorkEntry(prev => ({ ...prev, endDate: e.target.value }))}
                          disabled={newWorkEntry.isCurrent}
                          className="flex-1 rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm disabled:bg-bw-surface-subtle"
                          placeholder="MM/YYYY or Year"
                        />
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={newWorkEntry.isCurrent}
                            onChange={(e) => setNewWorkEntry(prev => ({ ...prev, isCurrent: e.target.checked, endDate: '' }))}
                            className="rounded border-bw-border text-bw-primary mr-1"
                          />
                          Current
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-bw-ink mb-1">Description</label>
                    <textarea
                      value={newWorkEntry.description}
                      onChange={(e) => setNewWorkEntry(prev => ({ ...prev, description: e.target.value }))}
                      rows={2}
                      className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary text-sm"
                      placeholder="Describe responsibilities and key contributions..."
                    />
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={addWorkHistoryEntry}
                      className="flex items-center px-3 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 text-sm"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Experience
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Education History Section */}
          <div className="mt-6 border-t pt-6">
            <button
              type="button"
              onClick={() => toggleSection('educationHistory')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <GraduationCap className="h-5 w-5 text-bw-primary mr-2" />
                <span className="text-lg font-semibold text-bw-ink">Education History</span>
                <span className="ml-2 text-sm text-bw-muted">({currentEducationHistory.length} entries)</span>
              </div>
              {expandedSections.educationHistory ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.educationHistory && (
              <div className="mt-4 space-y-4">
                {/* Existing education entries */}
                {currentEducationHistory.map((entry, index) => (
                  <div key={index} className="bg-bw-surface-subtle rounded-lg p-4 border relative">
                    <button
                      type="button"
                      onClick={() => removeEducationEntry(index)}
                      className="absolute top-2 right-2 text-bw-muted hover:text-urgent"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="pr-8">
                      <h4 className="font-semibold text-bw-ink">{entry.degree}</h4>
                      <p className="text-sm text-bw-primary">{entry.institution}</p>
                      <p className="text-xs text-bw-muted mt-1">
                        {entry.field && `${entry.field} | `}
                        {entry.graduationYear && `Class of ${entry.graduationYear}`}
                      </p>
                      {(entry.honors || entry.gpa) && (
                        <p className="text-xs text-bw-muted mt-1">
                          {entry.honors && <span className="text-fit-moderate">{entry.honors}</span>}
                          {entry.honors && entry.gpa && ' | '}
                          {entry.gpa && `GPA: ${entry.gpa}`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add new education entry form */}
                <div className="bg-bw-primary-tint rounded-lg p-4 border border-indigo-200">
                  <h4 className="font-medium text-bw-ink mb-3 flex items-center">
                    <Plus className="h-4 w-4 mr-2 text-bw-primary" />
                    Add Education
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Institution *</label>
                      <input
                        type="text"
                        value={newEducationEntry.institution}
                        onChange={(e) => setNewEducationEntry(prev => ({ ...prev, institution: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        placeholder="University or school name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Degree *</label>
                      <input
                        type="text"
                        value={newEducationEntry.degree}
                        onChange={(e) => setNewEducationEntry(prev => ({ ...prev, degree: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        placeholder="e.g., Bachelor of Science, MBA"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Field of Study</label>
                      <input
                        type="text"
                        value={newEducationEntry.field}
                        onChange={(e) => setNewEducationEntry(prev => ({ ...prev, field: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        placeholder="e.g., Computer Science"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Graduation Year</label>
                      <input
                        type="number"
                        value={newEducationEntry.graduationYear || ''}
                        onChange={(e) => setNewEducationEntry(prev => ({ ...prev, graduationYear: e.target.value ? parseInt(e.target.value) : null }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        placeholder="YYYY"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">Honors/Awards</label>
                      <input
                        type="text"
                        value={newEducationEntry.honors || ''}
                        onChange={(e) => setNewEducationEntry(prev => ({ ...prev, honors: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        placeholder="e.g., Magna Cum Laude"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-bw-ink mb-1">GPA</label>
                      <input
                        type="text"
                        value={newEducationEntry.gpa || ''}
                        onChange={(e) => setNewEducationEntry(prev => ({ ...prev, gpa: e.target.value }))}
                        className="w-full rounded-md border-bw-border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
                        placeholder="e.g., 3.8/4.0"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={addEducationEntry}
                      className="flex items-center px-3 py-2 bg-bw-primary text-white rounded-md hover:bg-indigo-700 text-sm"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Education
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Military Service Section */}
          <div className="mt-6 border-t pt-6">
            <button
              type="button"
              onClick={() => toggleSection('militaryService')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <Medal className="h-5 w-5 text-success mr-2" />
                <span className="text-lg font-semibold text-bw-ink">Military Service</span>
                {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService) && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-success-bg text-success">Veteran</span>
                )}
              </div>
              {expandedSections.militaryService ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.militaryService && (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-bw-muted">
                  Military service experience is valuable for federal contracting and can qualify for veteran-owned business set-asides.
                </p>

                {/* Current military service data display */}
                {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService) ? (
                  <div className="bg-success-bg rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-bw-ink flex items-center">
                        <Medal className="h-4 w-4 mr-2 text-success" />
                        {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.branch}
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          if (editingIndex !== null) {
                            const updated = [...personnel];
                            updated[editingIndex] = { ...updated[editingIndex], militaryService: null };
                            setPersonnel(updated);
                          } else {
                            setNewPerson(prev => ({ ...prev, militaryService: null }));
                          }
                        }}
                        className="text-urgent hover:text-urgent"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-bw-muted">
                      <div><span className="font-medium">Rank:</span> {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.rank}</div>
                      <div><span className="font-medium">Specialty:</span> {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.specialty}</div>
                      <div><span className="font-medium">Service:</span> {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.startDate} - {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.endDate}</div>
                      <div><span className="font-medium">Discharge:</span> {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.dischargeType}</div>
                    </div>
                    {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.honors &&
                     (editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)!.honors.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(editingIndex !== null ? personnel[editingIndex]?.militaryService : newPerson.militaryService)?.honors.map((honor, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-success-bg text-success rounded-full">{honor}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-bw-surface-subtle rounded-lg p-4 border border-bw-border">
                    <h4 className="font-medium text-bw-ink mb-3 flex items-center">
                      <Plus className="h-4 w-4 mr-2 text-success" />
                      Add Military Service
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Branch of Service *</label>
                        <select
                          id="military-branch"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        >
                          <option value="">Select branch...</option>
                          <option value="Army">U.S. Army</option>
                          <option value="Navy">U.S. Navy</option>
                          <option value="Air Force">U.S. Air Force</option>
                          <option value="Marines">U.S. Marine Corps</option>
                          <option value="Coast Guard">U.S. Coast Guard</option>
                          <option value="Space Force">U.S. Space Force</option>
                          <option value="National Guard">National Guard</option>
                          <option value="Reserves">Reserves</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Rank at Separation</label>
                        <input
                          type="text"
                          id="military-rank"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                          placeholder="e.g., E-5 (Sergeant), O-3 (Captain)"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">MOS/Rating/AFSC</label>
                        <input
                          type="text"
                          id="military-specialty"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                          placeholder="e.g., 25B (IT Specialist)"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Years of Service</label>
                        <input
                          type="number"
                          id="military-years"
                          min="0"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                          placeholder="e.g., 6"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Start Date</label>
                        <input
                          type="text"
                          id="military-start"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                          placeholder="MM/YYYY"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">End Date</label>
                        <input
                          type="text"
                          id="military-end"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                          placeholder="MM/YYYY or Present"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Discharge Type</label>
                        <select
                          id="military-discharge"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                        >
                          <option value="">Select...</option>
                          <option value="Honorable">Honorable</option>
                          <option value="General">General (Under Honorable)</option>
                          <option value="Active">Currently Active</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-bw-ink mb-1">Honors/Decorations</label>
                        <input
                          type="text"
                          id="military-honors"
                          className="w-full rounded-md border-bw-border shadow-sm focus:border-green-500 focus:ring-green-500 text-sm"
                          placeholder="e.g., Bronze Star, Purple Heart"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-4">
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          id="military-scd"
                          className="rounded border-bw-border text-success focus:ring-green-500 mr-2"
                        />
                        Service-Connected Disability
                      </label>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          const branch = (document.getElementById('military-branch') as HTMLSelectElement)?.value;
                          const rank = (document.getElementById('military-rank') as HTMLInputElement)?.value;
                          const specialty = (document.getElementById('military-specialty') as HTMLInputElement)?.value;
                          const years = parseInt((document.getElementById('military-years') as HTMLInputElement)?.value) || 0;
                          const startDate = (document.getElementById('military-start') as HTMLInputElement)?.value;
                          const endDate = (document.getElementById('military-end') as HTMLInputElement)?.value;
                          const discharge = (document.getElementById('military-discharge') as HTMLSelectElement)?.value;
                          const honors = (document.getElementById('military-honors') as HTMLInputElement)?.value;
                          const scd = (document.getElementById('military-scd') as HTMLInputElement)?.checked;

                          if (!branch) {
                            toast.error('Please select a branch of service');
                            return;
                          }

                          const militaryData: MilitaryServiceEntry = {
                            branch,
                            rank: rank || '',
                            specialty: specialty || '',
                            yearsOfService: years,
                            startDate: startDate || '',
                            endDate: endDate || '',
                            dischargeType: discharge || '',
                            honors: honors ? honors.split(',').map(h => h.trim()) : [],
                            isVeteran: true,
                            serviceConnectedDisability: scd,
                            deployments: []
                          };

                          if (editingIndex !== null) {
                            const updated = [...personnel];
                            updated[editingIndex] = { ...updated[editingIndex], militaryService: militaryData };
                            setPersonnel(updated);
                          } else {
                            setNewPerson(prev => ({ ...prev, militaryService: militaryData }));
                          }

                          toast.success('Military service added');
                        }}
                        className="flex items-center px-3 py-2 bg-success text-white rounded-md hover:bg-green-700 text-sm"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Military Service
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Additional Information Section */}
          <div className="mt-6 border-t pt-6">
            <button
              type="button"
              onClick={() => toggleSection('additionalInfo')}
              className="flex items-center justify-between w-full text-left"
            >
              <div className="flex items-center">
                <Star className="h-5 w-5 text-fit-moderate mr-2" />
                <span className="text-lg font-semibold text-bw-ink">Additional Information</span>
              </div>
              {expandedSections.additionalInfo ? (
                <ChevronUp className="h-5 w-5 text-bw-muted" />
              ) : (
                <ChevronDown className="h-5 w-5 text-bw-muted" />
              )}
            </button>

            {expandedSections.additionalInfo && (
              <div className="mt-4 space-y-4">
                {/* Languages */}
                <div>
                  <label className="block text-sm font-medium text-bw-ink mb-2 flex items-center">
                    <Globe className="h-4 w-4 mr-2 text-bw-muted" />
                    Languages
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(editingIndex !== null ? personnel[editingIndex]?.languages || [] : newPerson.languages).map((lang, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-teal-100 text-teal-800">
                        {lang}
                        <button
                          type="button"
                          onClick={() => removeFromPersonnelArray('languages', index)}
                          className="ml-2 text-teal-600 hover:text-teal-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add language (e.g., Spanish - Fluent)..."
                      className="flex-1 rounded-md border-bw-border shadow-sm focus:border-teal-500 focus:ring-teal-500 text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addToPersonnelArray('languages' as any, e.currentTarget.value);
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                        addToPersonnelArray('languages' as any, input.value);
                        input.value = '';
                      }}
                      className="px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Publications */}
                <div>
                  <label className="block text-sm font-medium text-bw-ink mb-2 flex items-center">
                    <BookOpen className="h-4 w-4 mr-2 text-bw-muted" />
                    Publications & Presentations
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(editingIndex !== null ? personnel[editingIndex]?.publications || [] : newPerson.publications).map((pub, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-fit-moderate max-w-full">
                        <span className="truncate">{pub}</span>
                        <button
                          type="button"
                          onClick={() => removeFromPersonnelArray('publications', index)}
                          className="ml-2 text-fit-moderate hover:text-fit-moderate flex-shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add publication or presentation..."
                      className="flex-1 rounded-md border-bw-border shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addToPersonnelArray('publications' as any, e.currentTarget.value);
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                        addToPersonnelArray('publications' as any, input.value);
                        input.value = '';
                      }}
                      className="px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 text-sm"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Awards */}
                <div>
                  <label className="block text-sm font-medium text-bw-ink mb-2 flex items-center">
                    <Award className="h-4 w-4 mr-2 text-bw-muted" />
                    Awards & Recognitions
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(editingIndex !== null ? personnel[editingIndex]?.awards || [] : newPerson.awards).map((award, index) => (
                      <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-fit-moderate-bg text-fit-moderate max-w-full">
                        <span className="truncate">{award}</span>
                        <button
                          type="button"
                          onClick={() => removeFromPersonnelArray('awards', index)}
                          className="ml-2 text-fit-moderate hover:text-fit-moderate flex-shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add award or recognition..."
                      className="flex-1 rounded-md border-bw-border shadow-sm focus:border-amber-500 focus:ring-amber-500 text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addToPersonnelArray('awards' as any, e.currentTarget.value);
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                        addToPersonnelArray('awards' as any, input.value);
                        input.value = '';
                      }}
                      className="px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingIndex(null);
              }}
              className="px-4 py-2 border border-bw-border rounded-md text-bw-ink hover:bg-bw-surface-subtle"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (editingIndex !== null) {
                  handleUpdatePerson(editingIndex);
                } else {
                  handleAddPerson();
                }
              }}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Saving...' : editingIndex !== null ? 'Update Person' : 'Add Person'}
            </button>
          </div>
        </div>
      )}

      {/* Personnel List */}
      <div className="space-y-4">
        {personnel.map((person, index) => (
          <div key={person.id || index} className="bg-bw-surface rounded-lg border p-6 shadow-sm">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-4 mb-3">
                  <h3 className="text-lg font-semibold text-bw-ink">{person.name}</h3>
                  <span className="text-sm text-bw-muted">{person.title}</span>
                  {person.securityClearance !== 'none' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-primary-tint text-bw-primary">
                      <Shield className="h-3 w-3 mr-1" />
                      {person.securityClearance}
                    </span>
                  )}
                  {person.militaryService && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-success-bg text-success">
                      <Medal className="h-3 w-3 mr-1" />
                      Veteran - {person.militaryService.branch}
                    </span>
                  )}
                  {person.linkedinUrl && (
                    <a
                      href={person.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-[#0077B5]/10 text-[#0077B5] hover:bg-[#0077B5]/20"
                    >
                      <Linkedin className="h-3 w-3 mr-1" />
                      LinkedIn
                    </a>
                  )}
                </div>

                {/* Professional Summary */}
                {person.professionalSummary && (
                  <p className="text-sm text-bw-muted mb-3 line-clamp-2">{person.professionalSummary}</p>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Experience:</span>
                    <span className="ml-2 text-sm text-bw-muted">{person.yearsExperience} years</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Education:</span>
                    <span className="ml-2 text-sm text-bw-muted">{person.educationLevel || 'Not specified'}</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Availability:</span>
                    <span className="ml-2 text-sm text-bw-muted">{person.availability}%</span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-bw-ink">Contact:</span>
                    <span className="ml-2 text-sm text-bw-muted">{person.email}</span>
                  </div>
                </div>

                {/* Resume completeness indicators */}
                <div className="mt-3 flex flex-wrap gap-2">
                  {person.workHistory && person.workHistory.length > 0 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-primary-tint text-bw-primary border border-blue-200">
                      <Briefcase className="h-3 w-3 mr-1" />
                      {person.workHistory.length} work experience{person.workHistory.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {person.educationHistory && person.educationHistory.length > 0 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-primary-tint text-bw-primary border border-indigo-200">
                      <GraduationCap className="h-3 w-3 mr-1" />
                      {person.educationHistory.length} education record{person.educationHistory.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {person.certifications && person.certifications.length > 0 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-primary-tint text-bw-primary border border-purple-200">
                      <Award className="h-3 w-3 mr-1" />
                      {person.certifications.length} certification{person.certifications.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {person.languages && person.languages.length > 0 && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-teal-50 text-teal-700 border border-teal-200">
                      <Globe className="h-3 w-3 mr-1" />
                      {person.languages.length} language{person.languages.length > 1 ? 's' : ''}
                    </span>
                  )}
                  {person.militaryService && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-success-bg text-success border border-green-200">
                      <Medal className="h-3 w-3 mr-1" />
                      {person.militaryService.yearsOfService} yrs military service
                    </span>
                  )}
                </div>

                {person.skillsExpertise && person.skillsExpertise.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-bw-ink">Skills:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {person.skillsExpertise.slice(0, 8).map((skill, skillIndex) => (
                        <span key={skillIndex} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-bw-primary-tint text-bw-primary">
                          {skill}
                        </span>
                      ))}
                      {person.skillsExpertise.length > 8 && (
                        <span className="text-xs text-bw-muted self-center">
                          +{person.skillsExpertise.length - 8} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingIndex(index)}
                  className="p-2 text-bw-muted hover:text-bw-primary"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDeletePerson(index)}
                  className="p-2 text-bw-muted hover:text-urgent"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {personnel.length === 0 && !showAddForm && (
          <div className="text-center py-8 bg-bw-surface-subtle rounded-lg">
            <Users className="h-12 w-12 text-bw-muted mx-auto mb-3" />
            <p className="text-bw-muted">No personnel added yet</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-3 text-bw-primary hover:text-bw-primary font-medium"
            >
              Add your first team member
            </button>
          </div>
        )}
      </div>
    </div>
  );
}